"use client";
// ===================================================
// DevEnvBar — universal "developer environment" strip, shown ONLY on vdev.
// Sits in the ROOT layout above every page; its presence means you're on the
// developer site (vdev), not production. It is the SINGLE control surface: page
// picker + state pair (A vs B) + view mode + a compare toggle. The overlay it
// opens has NO controls of its own — just the comparison (EnvCompareViewer) and
// the facts header. On vbrain.io it renders nothing.
// ===================================================
import { useEffect, useMemo, useState, useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import { FlipHorizontal2, Columns2, SplitSquareHorizontal, GitCompare, Gauge, X, GitBranch, Loader2, Table2, Users } from "lucide-react";
import { NAV_GROUPS, type NavEntry } from "@/components/command-center/Sidebar";
import { Button } from "@/components/ui";
import { PreviewViewersOverlay } from "./PreviewViewersOverlay";
import { DevStatusBoard } from "@/components/system/DevStatusBoard";
import { ENVS, ENV_STATUS, type EnvKey, useEnvStates } from "@/lib/environments-queries";
import { EnvStatesHeader } from "@/components/environments/EnvStatesHeader";
import { EnvCompareViewer, type CompareMode } from "@/components/environments/EnvCompareViewer";
import { DEV_ENV_OFFSET_VAR, shellChromeTop } from "@/lib/shellChromeOffsets";
import { BranchTableOverlay } from "@/components/environments/BranchTableOverlay";
import type { BranchInfo } from "@/lib/vdev/branch-rows";
import { isDeveloperHost } from "@/lib/auth/developer-shell-host";

const BAR_H = 36; // px — matches h-9; keep in sync with the offset <style>
const STORE = "env-compare-prefs-v3";
const SKIP = ["/landing", "/auth", "/sign", "/verify", "/watch", "/shared"];

// Flatten the real sidebar nav into selectable routes — Rule 10, no dup list.
function useRoutes(): string[] {
  return useMemo(() => {
    const seen = new Set<string>(); const out: string[] = [];
    const add = (href?: string) => {
      if (!href || seen.has(href)) return;
      if (!href.startsWith("/dashboard") && !href.startsWith("/internal-tools")) return;
      seen.add(href); out.push(href);
    };
    for (const g of NAV_GROUPS) for (const e of g.items as NavEntry[]) {
      add(e.href);
      if ("children" in e && Array.isArray(e.children)) for (const c of e.children) add(c.href);
    }
    return out.sort();
  }, []);
}


// A stage the bar offers but that is not actually wired must SAY so in the option
// itself — otherwise Preview reads as a peer of Edit and you only discover it is a
// placeholder after selecting it. ENV_STATUS is already the authored honesty
// vocabulary (עובד · חלקי · קריא בלבד · לא מחובר עדיין); only "ok" stays bare.
const stageOption = (k: EnvKey) =>
  ENV_STATUS[k].tone === "ok" ? ENVS[k].label : `${ENVS[k].label} · ${ENV_STATUS[k].label}`;

// "לפני 3 ימים" — same shape as ago() in environments-queries, but tolerant of
// git's ISO-8601-with-offset and of the field being absent entirely.
function agoShort(iso?: string): string {
  if (!iso) return "";
  const t = new Date(iso.replace(" ", "T").replace(/ ([+-]\d{4})$/, "$1")).getTime();
  if (!Number.isFinite(t)) return "";
  const d = Math.round((Date.now() - t) / 86400000);
  if (d <= 0) return "היום";
  if (d === 1) return "אתמול";
  if (d < 30) return `לפני ${d} ימים`;
  return `לפני ${Math.round(d / 30)} חודשים`;
}

// The one-line spec that goes NEXT TO the branch name in the dropdown: what is
// in there, how far it drifted from main, and how stale it is. Gal's ask —
// a branch name alone does not say what you will find inside it.
// הבורר המהיר נושא זהות בלבד: שם וזמן. כל השאר — סוג, אפליקציה, סטטוס
// הפיתוח, התיאור והסחיפה — עבר לטבלה, שבה אפשר לסנן, למיין ולחפש.
// גל, 06.09.2026: «לא שורות ארוכות של מידע».
// מראה אחד לשני כפתורי הפעולה בפאנל «מקור/גרסה» — פתיחת הטבלה והמעבר
// לענף. היו שתי כיתובות כמעט זהות בגוונים שונים; זהו מקור אחד לשתיהן.
const SKY_ACTION_BTN =
  "flex items-center justify-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[11px] font-medium text-sky-200 transition-colors hover:bg-sky-500/20";

function branchLine(b: BranchInfo): string {
  const when = agoShort(b.date);
  return when ? `${b.name}  ·  ${when}` : b.name;
}

const MODES: { key: CompareMode; label: string; icon: React.ElementType }[] = [
  { key: "side", label: "זה לצד זה", icon: SplitSquareHorizontal },
  { key: "slider", label: "סליידר", icon: FlipHorizontal2 },
  { key: "toggle", label: "טוגל", icon: Columns2 },
];

function CompareOverlay({ route, a, b, mode, refreshKey, onClose }: { route: string; a: EnvKey; b: EnvKey; mode: CompareMode; refreshKey: number; onClose: () => void }) {
  const { items, drift, loading } = useEnvStates();
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div dir="rtl" className="fixed inset-x-0 bottom-0 top-9 z-[57] flex flex-col bg-[#0a0a0a]/97 backdrop-blur-sm text-white">
      {/* facts collapsed by default — keeps the panes tall; expand for commit/drift.
          No outer scroll: the only scroll is inside each iframe (fixes the jank). */}
      {!loading && (
        <details className="shrink-0 border-b border-white/10 px-4 py-1.5">
          <summary className="cursor-pointer select-none text-xs text-slate-400">פרטי גרסאות (commit · drift) ▾</summary>
          <div className="pt-2"><EnvStatesHeader items={items} drift={drift} /></div>
        </details>
      )}
      <div className="min-h-0 flex-1 p-3">
        <EnvCompareViewer route={route} a={a} b={b} mode={mode} refreshKey={refreshKey} />
      </div>
    </div>
  );
}

// A non-destructive scroll-over: opens the full Dev Status board (incl. GitCockpit)
// ON TOP of the current page — no navigation, no URL change, dimmed page behind,
// Escape / backdrop / close to dismiss and return to your exact spot. (Gal's design.)
function CockpitOverlay({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div dir="rtl" onClick={onClose}
      className="fixed inset-x-0 bottom-0 top-9 z-[57] flex justify-center overflow-y-auto bg-black/60 backdrop-blur-sm">
      <div onClick={(e) => e.stopPropagation()}
        className="my-4 h-fit w-full max-w-4xl rounded-2xl border border-teal-500/20 bg-[#0b0f10] p-4 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-200">🛠️ מצב פיתוח</h2>
          <button type="button" onClick={onClose}
            className="flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-slate-300 transition-colors hover:bg-white/10">
            <X size={12} /> סגור
          </button>
        </div>
        <DevStatusBoard />
      </div>
    </div>
  );
}

export function DevEnvBar() {
  const pathname = usePathname();
  const router = useRouter();
  const routes = useRoutes();
  const [host, setHost] = useState<string | null>(null);
  const [developerAccess, setDeveloperAccess] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [route, setRoute] = useState("/dashboard/pinkas-100");
  const [a, setA] = useState<EnvKey>("edit");
  const [b, setB] = useState<EnvKey>("production");
  const [mode, setMode] = useState<CompareMode>("side");
  const [srcInfo, setSrcInfo] = useState(false);
  const [cockpitOpen, setCockpitOpen] = useState(false);
  const [branchTableOpen, setBranchTableOpen] = useState(false);
  const [viewersOpen, setViewersOpen] = useState(false);

  // Branch switcher — docs/specs/vdev-project-branch-switcher.md.
  const [branches, setBranches] = useState<BranchInfo[]>([]);
  const [switcherStatus, setSwitcherStatus] = useState<{ branch: string; commit: string } | null>(null);
  const [selectedBranchState, setSelectedBranch] = useState("");
  const selectedBranch = selectedBranchState;
  const [switching, setSwitching] = useState(false);
  const [switchMsg, setSwitchMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadSwitcherInfo = useCallback(() => {
    // Enriched listing first — it carries WHAT each branch holds, not just its name.
    // Falls back to the name-only endpoint so the panel still works against an
    // older switcher build (the service lives on the VPS and deploys separately).
    fetch("/api/vdev-switcher/branches/detail").then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { if (Array.isArray(d)) setBranches(d as BranchInfo[]); else throw new Error(); })
      .catch(() =>
        fetch("/api/vdev-switcher/branches").then((r) => r.json())
          .then((d) => Array.isArray(d) && setBranches((d as string[]).map((name) => ({ name }))))
          .catch(() => { /* switcher unreachable — panel just shows no options */ }));
    fetch("/api/vdev-switcher/status").then((r) => r.json()).then((d) => d?.branch && setSwitcherStatus(d))
      .catch(() => { /* switcher unreachable — panel just shows "loading" state */ });
  }, []);
  // הענף נטען עם עליית הסרגל ולא רק בפתיחת הפאנל — אחרת התווית על הכפתור
  // אומרת "מקור/גרסה" עד שלוחצים, ומי שמסתכל על vdev אינו יודע מה הוא רואה.
  useEffect(() => { if (developerAccess === true) loadSwitcherInfo(); }, [developerAccess, loadSwitcherInfo]);
  useEffect(() => { if (developerAccess === true && srcInfo) loadSwitcherInfo(); }, [developerAccess, srcInfo, loadSwitcherInfo]);

  const doSwitch = useCallback(async (branchArg?: string) => {
    const selectedBranch = branchArg ?? selectedBranchState;
    if (!selectedBranch || switching) return;
    setSwitching(true);
    setSwitchMsg(null);
    try {
      const res = await fetch("/api/vdev-switcher/switch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ branch: selectedBranch }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setSwitchMsg({ kind: "ok", text: `עברת ל-${data.branch}` });
        setSwitcherStatus({ branch: data.branch, commit: "" });
        setRefreshKey((k) => k + 1);
        loadSwitcherInfo();
      } else {
        setSwitchMsg({ kind: "err", text: data.error || "switch נכשל" });
      }
    } catch {
      setSwitchMsg({ kind: "err", text: "vdev-switcher לא זמין" });
    } finally {
      setSwitching(false);
    }
  }, [selectedBranchState, switching, loadSwitcherInfo]);

  const [embedded, setEmbedded] = useState(false);
  useEffect(() => {
    setHost(window.location.host);
    // Don't show the bar when the app is rendered INSIDE a compare pane (iframe) —
    // otherwise the preview pane nests its own dev bar (bar-in-bar).
    try { setEmbedded(window.top !== window.self); } catch { setEmbedded(true); }
  }, []);
  useEffect(() => {
    fetch("/api/auth/developer-shell", { cache: "no-store", credentials: "include" })
      .then((res) => res.json())
      .then((data) => setDeveloperAccess(data?.enabled === true))
      .catch(() => setDeveloperAccess(false));
  }, []);
  // default the compare route to whatever page you're on (until you change it)
  useEffect(() => { if (!open && pathname) setRoute(pathname); }, [pathname, open]);
  // restore lasting prefs (pair + mode)
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem(STORE) || "{}");
      if (p.a) setA(p.a); if (p.b) setB(p.b); if (p.mode) setMode(p.mode);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { localStorage.setItem(STORE, JSON.stringify({ a, b, mode })); }, [a, b, mode]);

  // Freshest first: with 163 branches, alphabetical order buries this week's work.
  const sortedBranches = useMemo(
    () => [...branches].sort((x, y) => (y.date ?? "").localeCompare(x.date ?? "") || x.name.localeCompare(y.name)),
    [branches],
  );
  const selected = branches.find((b) => b.name === selectedBranch);

  const onDeveloperHost = host ? isDeveloperHost(host) : false;
  const skip = SKIP.some((p) => (pathname ?? "").startsWith(p));
  if (developerAccess !== true || !onDeveloperHost || skip || embedded) return null;

  const sel = "rounded-md border border-white/[0.10] bg-white/[0.04] px-2 py-0.5 text-[11px] text-slate-200 outline-none focus:border-teal-500/50";

  return (
    <>
      {/* Shift dashboard chrome down — same mechanism as EditModeBanner (Rule 10). */}
      <style>{`
        :root { ${DEV_ENV_OFFSET_VAR}: ${BAR_H}px; }
        [data-cc-id="topbar.root"] { top: ${shellChromeTop(0)} !important; transition: top 180ms ease; }
        [data-cc-id="capsule.root"] { top: ${shellChromeTop(8)} !important; transition: top 180ms ease; }
        [data-cc-id="shell.root"] { padding-top: ${shellChromeTop(0)}; transition: padding-top 180ms ease; }
      `}</style>

      <div className="fixed inset-x-0 top-0 z-[58] flex h-9 items-center gap-2 border-b border-teal-500/20 bg-[#0b0f10]/95 px-3 backdrop-blur-md ps-36" dir="rtl">
        <span data-info-id="env-bar" className="flex items-center gap-1.5 text-[11px] font-semibold text-teal-300 shrink-0">
          <span className="h-1.5 w-1.5 rounded-full bg-teal-400 animate-pulse" />
          סביבת פיתוח · vdev
        </span>

        {/* page */}
        <label className="text-[11px] text-slate-500 shrink-0">עמוד</label>
        <select data-info-id="env-route" className={`${sel} max-w-[40vw]`} dir="ltr" value={routes.includes(route) ? route : ""}
          onChange={(e) => { const v = e.target.value; setRoute(v); if (!open) router.push(v); }}>
          {!routes.includes(route) && <option value="">{route}</option>}
          {routes.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>

        <span className="mx-0.5 h-4 w-px bg-white/10 shrink-0" />

        {/* state pair */}
        <select data-info-id="env-pair" className={sel} value={a} onChange={(e) => setA(e.target.value as EnvKey)}>
          {(Object.keys(ENVS) as EnvKey[]).map((k) => <option key={k} value={k} title={ENV_STATUS[k].note ?? ENVS[k].limitation}>{stageOption(k)}</option>)}
        </select>
        <span className="text-[11px] text-slate-500 shrink-0">מול</span>
        <select data-info-id="env-pair" className={sel} value={b} onChange={(e) => setB(e.target.value as EnvKey)}>
          {(Object.keys(ENVS) as EnvKey[]).map((k) => <option key={k} value={k} title={ENV_STATUS[k].note ?? ENVS[k].limitation}>{stageOption(k)}</option>)}
        </select>

        <span className="mx-0.5 h-4 w-px bg-white/10 shrink-0" />

        {/* mode */}
        <div data-info-id="env-modes" className="flex items-center gap-0.5 rounded-md border border-white/[0.08] bg-white/[0.02] p-0.5 shrink-0">
          {MODES.map((m) => {
            const Icon = m.icon; const active = mode === m.key;
            return (
              <button key={m.key} onClick={() => setMode(m.key)} title={m.label}
                className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] transition-colors ${active ? "bg-teal-500/20 text-teal-200" : "text-slate-400 hover:text-slate-200"}`}>
                <Icon size={12} />
              </button>
            );
          })}
        </div>

        <span className="mx-0.5 h-4 w-px bg-white/10 shrink-0" />
        <div className="relative shrink-0">
          <button type="button" onClick={() => setSrcInfo((v) => !v)} title="מקור/גרסה — עבור ל-vdev ענף אחר"
            className={`flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition-colors ${srcInfo ? "border-sky-400/60 bg-sky-500/20 text-sky-100" : "border-sky-500/30 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20"}`}>
            <GitBranch size={11} className="shrink-0" />
            <span dir="ltr" className="max-w-[22ch] truncate">{switcherStatus?.branch ?? "מקור/גרסה"}</span>
          </button>
          {srcInfo && (
            <div dir="rtl" className="absolute right-0 top-[130%] z-[60] w-80 rounded-lg border border-sky-500/30 bg-[#0b0f10]/98 p-3 text-[11px] text-slate-300 shadow-xl backdrop-blur-md">
              <div className="mb-2 font-semibold text-sky-300">vdev — ענף פעיל</div>

              {switcherStatus ? (
                <div className="mb-3 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5">
                  <div className="text-slate-200">{switcherStatus.branch}</div>
                  {switcherStatus.commit && <div className="text-[10px] text-slate-500">{switcherStatus.commit}</div>}
                </div>
              ) : (
                <div className="mb-3 text-slate-500">טוען מצב נוכחי…</div>
              )}

              <button type="button" onClick={() => { setBranchTableOpen(true); setSrcInfo(false); }}
                className={`mb-2 w-full py-1.5 ${SKY_ACTION_BTN}`}>
                <Table2 size={12} /> טבלת כל הענפים — חיפוש, מיון וסינון
              </button>

              <div className="mb-2 flex items-center gap-1.5">
                <select dir="ltr" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}
                  className="min-w-0 flex-1 rounded-md border border-white/[0.10] bg-white/[0.04] px-1.5 py-1 text-[11px] text-slate-200 outline-none focus:border-sky-500/50">
                  <option value="">בחר ענף…</option>
                  {sortedBranches.map((b) => <option key={b.name} value={b.name}>{branchLine(b)}</option>)}
                </select>
                <button type="button" onClick={() => void doSwitch()} disabled={!selectedBranch || switching}
                  className={`shrink-0 ${SKY_ACTION_BTN} disabled:cursor-not-allowed disabled:opacity-40`}>
                  {switching ? <Loader2 size={11} className="animate-spin" /> : null} עבור
                </button>
              </div>

              {/* מפרט הענף שנבחר — מה יש בו לפני שעוברים אליו */}
              {selected && (
                <div className="mb-2 rounded-md border border-sky-500/20 bg-sky-500/[0.06] px-2 py-1.5 leading-relaxed">
                  {selected.subject
                    ? <div className="text-slate-200">{selected.subject}</div>
                    : <div className="text-slate-500">אין תיאור — המתג עדיין מחזיר שמות בלבד</div>}
                  <div className="mt-1 flex flex-wrap gap-x-3 text-[10px] text-slate-400">
                    {selected.author && <span>{selected.author}</span>}
                    {selected.date && <span>{agoShort(selected.date)}</span>}
                    {selected.sha && <span dir="ltr">{selected.sha}</span>}
                    {selected.ahead !== undefined && (
                      <span dir="ltr">{selected.ahead}↑ {selected.behind ?? 0}↓ vs main</span>
                    )}
                  </div>
                </div>
              )}

              {switchMsg && (
                <div className={`rounded-md px-2 py-1 text-[10px] ${switchMsg.kind === "ok" ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"}`}>
                  {switchMsg.text}
                </div>
              )}

              <div className="mt-2 border-t border-white/10 pt-2 text-[10px] leading-relaxed text-slate-500">
                כל מי שרואה vdev יכול לעבור ענף — השינוי גלוי לכולם (Event Bus). Commit = שמירה · Push = GitHub + VPS Preview · Deploy = Vercel Production
              </div>
            </div>
          )}
        </div>

        <span className="flex-1" />

        {/* who may open preview.vbrain.io — the app login is its only gate */}
        <Button data-info-id="env-preview-viewers" variant="secondary" size="sm" icon={Users}
          onClick={() => setViewersOpen(true)} title="מי רשאי לראות Preview" className="shrink-0">
          מורשי Preview
        </Button>

        {/* cockpit — opens the full Dev Status board as a scroll-over (Gal's design) */}
        <button data-info-id="env-cockpit" onClick={() => setCockpitOpen(true)}
          className="flex items-center gap-1 rounded-md border border-teal-500/40 bg-teal-500/15 px-2.5 py-0.5 text-[11px] font-medium text-teal-200 transition-colors hover:bg-teal-500/25 shrink-0">
          <Gauge size={12} /> מצב פיתוח
        </button>

        {/* compare toggle */}
        <button data-info-id="env-compare" onClick={() => setOpen((o) => !o)}
          className={`flex items-center gap-1 rounded-md border px-2.5 py-0.5 text-[11px] font-medium transition-colors shrink-0 ${
            open ? "border-rose-500/40 bg-rose-500/10 text-rose-200" : "border-teal-500/40 bg-teal-500/15 text-teal-200 hover:bg-teal-500/25"}`}>
          {open ? <><X size={12} /> סגור</> : <><GitCompare size={12} /> השוואה</>}
        </button>
      </div>

      {open && <CompareOverlay route={route} a={a} b={b} mode={mode} refreshKey={refreshKey} onClose={() => setOpen(false)} />}
      {cockpitOpen && <CockpitOverlay onClose={() => setCockpitOpen(false)} />}
      {viewersOpen && <PreviewViewersOverlay onClose={() => setViewersOpen(false)} />}
      {branchTableOpen && (
        <BranchTableOverlay
          branches={branches}
          currentBranch={switcherStatus?.branch}
          switching={switching}
          onSwitch={(name) => { setSelectedBranch(name); void doSwitch(name); }}
          onClose={() => setBranchTableOpen(false)}
        />
      )}
    </>
  );
}
